import { useEffect, useState, type ReactNode } from "react";
import { MessageSquarePlus } from "lucide-react";
import type { LibraryEntry, NewComment } from "../api/contract";
import type { LibraryController } from "../hooks/useLibrary";
import { entriesForBinds, listOrders, sectionActionNote } from "../lib/panelTabs";
import type { SectionSummary } from "./PreviewFrame";
import { Item } from "./ContentView";
import { LANE_COPY } from "./LaneNote";

const PRESETS = ["Grid", "List", "Carousel", "Split", "Stacked"];

export function layoutRequest(preset: string): NonNullable<NewComment["request"]> {
  return { kind: "layout", preset: preset.toLowerCase() as "grid" | "list" | "carousel" | "split" | "stacked" };
}

export function sectionRequest(action: "hide" | "up" | "down" | "remove"): NonNullable<NewComment["request"]> {
  return { kind: "section", action };
}

interface SectionInspectorProps {
  section: SectionSummary;
  pageTitle: string;
  library: LibraryController;
  onRequest: (note: string, request: NonNullable<NewComment["request"]>) => Promise<boolean>;
  onVisibility: (hidden: boolean) => Promise<boolean>;
  onComment: () => void;
}

function Group({ title, lane, children }: { title: string; lane: "instant" | "requested"; children: ReactNode }) {
  return (
    <section className="bw-inspector-group">
      <div className="bw-inspector-group-head">
        <h3>{title}</h3>
        <span className="bw-lane-tag" data-lane={lane} title={lane === "instant" ? LANE_COPY.instant : LANE_COPY.requested}>
          <span className="bw-lane-mark" aria-hidden="true" />
          {lane === "instant" ? "Saves now" : "Stax does this"}
        </span>
      </div>
      {children}
    </section>
  );
}

/** The selected preview section's direct content and prepared requests. */
export function SectionInspector({ section, pageTitle, library, onRequest, onVisibility, onComment }: SectionInspectorProps) {
  const [added, setAdded] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [visibility, setVisibility] = useState<"saving" | "failed" | null>(null);
  useEffect(() => setVisibility(null), [section.nodeId]);
  const entries = entriesForBinds(library.library?.entries ?? [], section.binds);
  const orders = listOrders(entries);
  const request = async (key: string, note: string, payload: NonNullable<NewComment["request"]>) => {
    if (await onRequest(note, payload)) {
      setAdded(key);
      window.setTimeout(() => setAdded((current) => current === key ? null : current), 2200);
    }
  };

  const toggleVisibility = async () => {
    setVisibility("saving");
    setVisibility(await onVisibility(!section.hidden) ? null : "failed");
  };

  return (
    <div className="bw-inspector">
      <header className="bw-inspector-title"><h2>{section.label}</h2><p>{pageTitle}</p></header>
      <Group title="Content" lane="instant">
        {entries.length === 0 ? (
          <p className="bw-activity-empty">Nothing in this section can be edited here yet. Comment on it and Stax will change it.</p>
        ) : (
          <ul className="bw-items">{entries.map((entry: LibraryEntry) => <Item key={entry.id} entry={entry} library={library} listOrder={entry.list ? orders.get(entry.list.collectionId) : undefined} />)}</ul>
        )}
      </Group>
      <Group title="Visibility" lane="instant">
        <div className="bw-visibility">
          <p>{section.hidden ? "Hidden. Visitors don’t see this section." : "Showing on your site."}</p>
          <button type="button" className="bw-btn bw-btn--quiet" disabled={visibility === "saving"} onClick={() => void toggleVisibility()}>
            {visibility === "saving" ? "Saving…" : section.hidden ? "Show section" : "Hide section"}
          </button>
        </div>
        {visibility === "failed" && <p className="bw-change-error" role="alert">That wasn’t saved. Try again.</p>}
      </Group>
      <Group title="Layout" lane="requested">
        <div className="bw-preset-row" aria-label="Request a layout">
          {PRESETS.map((preset) => <button key={preset} type="button" className="bw-btn bw-btn--quiet" onClick={() => void request(`layout-${preset}`, `Change this section’s layout to ${preset}.`, layoutRequest(preset))}>{added === `layout-${preset}` ? "Added to your comments" : preset}</button>)}
        </div>
      </Group>
      <Group title="Section actions" lane="requested">
        <div className="bw-section-actions">
          <button type="button" className="bw-btn bw-btn--quiet" onClick={() => void request("up", sectionActionNote("up"), { kind: "section", action: "up" })}>{added === "up" ? "Added to your comments" : "Move up"}</button>
          <button type="button" className="bw-btn bw-btn--quiet" onClick={() => void request("down", sectionActionNote("down"), { kind: "section", action: "down" })}>{added === "down" ? "Added to your comments" : "Move down"}</button>
          {confirmRemove ? <span className="bw-remove-confirm"><span>Remove this section?</span><button type="button" className="bw-btn bw-btn--primary" onClick={() => { setConfirmRemove(false); void request("remove", sectionActionNote("remove"), sectionRequest("remove")); }}>Remove</button><button type="button" className="bw-btn bw-btn--quiet" onClick={() => setConfirmRemove(false)}>Cancel</button></span> : <button type="button" className="bw-btn bw-btn--quiet" onClick={() => setConfirmRemove(true)}>Remove</button>}
        </div>
      </Group>
      <button type="button" className="bw-btn bw-btn--quiet bw-comment-section" onClick={onComment}><MessageSquarePlus size={18} aria-hidden="true" />Comment on this section</button>
    </div>
  );
}
